import { getHrefFromCustomUrlPlugin, getLastCategory } from "@/helpers";
import { IndexCard, SpecificFields } from "@/types";
import { Card } from "@/types/General";
import { Content } from "@builder.io/sdk-react";
import { customComponents } from "@/libs/builder/customComponents";
import Link from "@/components/Link";

export const cardMapper = (
  card: { id?: string; data: IndexCard },
  specificFields?: SpecificFields,
  locale?: string,
): Card => {
  switch (card.data.type) {
    case "Resource": {
      const fullCategory = card.data?.categorization?.find(
        (category) => category.parameterName === "resourceType",
      )?.categories?.[0];
      const lastCategory = getLastCategory(fullCategory ?? "");
      return {
        type: "Resource",
        resourceType: lastCategory,
        index: card.data?.index,
        isFeatured: card.data?.isFeatured || false,
        title: card.data?.title ?? "",
        description: card.data?.description ?? "",
        link:
          card.data?.link.title && card.data?.link.url
            ? {
                title: card.data?.link.title,
                url: getHrefFromCustomUrlPlugin(card.data?.link.url),
                dlt: card.data?.link.dlt,
                dld: card.data?.link.dld,
              }
            : undefined,
        media: card.data?.media,
      };
    }
    case "Integration": {
      return {
        type: "Integration",
        isFeatured: card.data?.isFeatured || false,
        title: card.data?.title ?? "",
        description: card.data?.description ?? "",
        link:
          card.data?.link.title && card.data?.link.url
            ? {
                title: card.data?.link.title,
                url: getHrefFromCustomUrlPlugin(card.data?.link.url),
                dlt: card.data?.link.dlt,
                dld: card.data?.link.dld,
              }
            : undefined,
        media: card.data?.media,
      };
    }
    case "Download": {
      return {
        type: "Download",
        isFeatured: card.data?.isFeatured || false,
        title: card.data?.title ?? "",
        description: card.data?.description ?? "",
        link:
          card.data?.link.title && card.data?.link.url
            ? {
                title: card.data?.link.title,
                url: getHrefFromCustomUrlPlugin(card.data?.link.url),
                dlt: card.data?.link.dlt,
                dld: card.data?.link.dld,
                underTitle: card.data?.link.underTitle,
              }
            : undefined,
        secondLink: card.data?.secondLink?.title
          ? {
              title: card.data?.secondLink.title,
              url: card.data?.secondLink.url
                ? getHrefFromCustomUrlPlugin(card.data?.secondLink.url)
                : undefined,
              dlt: card.data?.secondLink.dlt,
              dld: card.data?.secondLink.dld,
            }
          : undefined,
        thirdLink:
          card.data?.thirdLink?.title && card.data?.thirdLink.url
            ? {
                title: card.data?.thirdLink.title,
                url: getHrefFromCustomUrlPlugin(card.data?.thirdLink.url),
                dlt: card.data?.thirdLink.dlt,
                dld: card.data?.thirdLink.dld,
              }
            : undefined,
        media: card.data?.media,
        modal: card.data?.modal?.value ? (
          <Content
            apiKey={process.env.NEXT_PUBLIC_BUILDER_API_KEY as string}
            model="symbol"
            locale={locale}
            content={card.data?.modal?.value}
            linkComponent={Link}
            customComponents={customComponents}
          />
        ) : undefined,
      };
    }
    case "Product": {
      return {
        type: "Product",
        title: card.data?.title ?? "",
        subhead: card.data?.subhead,
        description: card.data?.description ?? "",
        categorization: card.data?.categorization,
        link:
          card.data?.link?.title && card.data?.link?.url
            ? {
                title: card.data.link.title,
                url: getHrefFromCustomUrlPlugin(card.data.link.url),
                dlt: card.data.link.dlt,
                dld: card.data.link.dld,
                underTitle: card.data.link.underTitle,
              }
            : undefined,
        secondLink: card.data?.secondLink?.title
          ? {
              title: card.data.secondLink.title,
              url: card.data.secondLink.url
                ? getHrefFromCustomUrlPlugin(card.data.secondLink.url)
                : undefined,
              dlt: card.data.secondLink.dlt,
              dld: card.data.secondLink.dld,
            }
          : undefined,
        thirdLink: card.data?.thirdLink?.url
          ? {
              title: card.data.thirdLink.title,
              url: getHrefFromCustomUrlPlugin(card.data.thirdLink.url),
              dlt: card.data.thirdLink.dlt,
              dld: card.data.thirdLink.dld,
            }
          : undefined,
        modal: card.data?.modal?.value ? (
          <Content
            apiKey={process.env.NEXT_PUBLIC_BUILDER_API_KEY as string}
            model="symbol"
            locale={locale}
            content={card.data?.modal?.value}
            linkComponent={Link}
            customComponents={customComponents}
          />
        ) : undefined,
      };
    }
    case "Event": {
      const allCategories =
        card.data?.categorization?.flatMap((category) => category.categories) ??
        [];
      const promotedCategories =
        specificFields?.type === "Event"
          ? specificFields.promotedCategories
          : [];
      const maxPromotedCategories =
        specificFields?.type === "Event"
          ? specificFields.maxPromotedCategories
          : undefined;

      const categories =
        promotedCategories
          ?.filter((category) => allCategories.includes(category))
          .slice(0, maxPromotedCategories) ?? allCategories;

      return {
        type: "Event",
        startDate: card.data?.startDate ?? null,
        endDate: card.data?.endDate ?? null,
        location: card.data?.location ?? null,
        hideDate: card.data?.hideDate ?? true,
        isFeatured: card.data?.isFeatured || false,
        title: card.data?.title ?? "",
        description: card.data?.description ?? "",
        presenters: card.data?.presenters ?? [],
        tier: card.data?.tier ?? 1,
        categories,
        fomoText:
          specificFields?.type === "Event"
            ? specificFields.fomoText
            : undefined,
        link:
          card.data?.link.title && card.data?.link.url
            ? {
                title: card.data?.link.title,
                url: getHrefFromCustomUrlPlugin(card.data?.link.url),
                dlt: card.data?.link.dlt,
                dld: card.data?.link.dld,
              }
            : undefined,
        media: card.data?.media,
      };
    }
    case "Solution": {
      return {
        type: "Solution",
        isFeatured: card.data?.isFeatured || false,
        title: card.data?.title ?? "",
        description: card.data?.description ?? "",
        link:
          card.data?.link.title && card.data?.link.url
            ? {
                title: card.data?.link.title,
                url: getHrefFromCustomUrlPlugin(card.data?.link.url),
                dlt: card.data?.link.dlt,
                dld: card.data?.link.dld,
              }
            : undefined,
        media: card.data?.media,
      };
    }
    case "Newsroom": {
      return {
        type: "Newsroom",
        newsroomType: card.data?.newsroomType ?? null,
        date: card.data?.date ?? null,
        isFeatured: card.data?.isFeatured || false,
        title: card.data?.title ?? "",
        description: card.data?.description ?? "",
        link:
          card.data?.link.title && card.data?.link.url
            ? {
                title: card.data?.link.title,
                url: getHrefFromCustomUrlPlugin(card.data?.link.url),
                dlt: card.data?.link.dlt,
                dld: card.data?.link.dld,
              }
            : undefined,
        media: card.data?.media,
      };
    }
    case "Use Case": {
      return {
        type: "Use Case",
        isFeatured: card.data?.isFeatured || false,
        title: card.data?.title ?? "",
        description: card.data?.description ?? "",
        link:
          card.data?.link.title && card.data?.link.url
            ? {
                title: card.data?.link.title,
                url: getHrefFromCustomUrlPlugin(card.data?.link.url),
                dlt: card.data?.link.dlt,
                dld: card.data?.link.dld,
              }
            : undefined,
        media: card.data?.media,
      };
    }
    case "CVE": {
      return {
        type: "CVE",
        title: card.data?.title ?? "",
        index: card.data?.index ?? "",
        link:
          card.data?.link.title && card.data?.link.url
            ? {
                title: card.data?.link.title,
                url: getHrefFromCustomUrlPlugin(card.data?.link.url),
                dlt: card.data?.link.dlt,
                dld: card.data?.link.dld,
              }
            : undefined,
        categorization: card.data?.categorization,
        description: card.data?.description ?? "",
        date: card.data?.date ?? "",
        updatedDate: card.data?.updatedDate ?? null,
        fixedVersion: card.data?.fixedVersion ?? "",
        guid: card.data?.guid ?? "",
      };
    }
    default:
      return {} as Card;
  }
};
